/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtom,
  useStateXAtomSetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { memo } from 'react';
import { EMPTY_PAGE } from 'root/Resource';
import { useIoPagesEditStore, useIoPagesStore } from 'store/utils/utils';
import { maximizeSidebarWidthAtom } from '../../../../../utils/state';
import {
  PageView,
  sidebarActivePageIdAtom,
  sidebarPageWizardDataSourceAtom,
  sidebarPageWizardStepAtom,
  sidebarPageWizardTitleAtom,
} from '../../../atoms';
import { WizardOption } from '../../common';
import PageWizardChooseDatasource from './PageWizardChooseDatasource';
import PageWizardChooseTemplate, {
  PageWizardChooseTemplateWithStore,
} from './PageWizardChooseTemplate';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
    backgroundColor: theme.palette.background.default,
  },
}));

const PageWizard = ({
  setView,
  appUid,
  width,
}: {
  width: number;
  appUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement | null => {
  const classes = useStyles();
  const setTitle = useStateXAtomSetter(sidebarPageWizardTitleAtom);
  const set = useStateXSetter();
  const [step, setStep] = useStateXAtom(sidebarPageWizardStepAtom);
  const [dataSource, setDataSource] = useStateXAtom(
    sidebarPageWizardDataSourceAtom,
  );
  const listStore = useIoPagesStore(appUid);
  const editStore = useIoPagesEditStore(appUid);

  if (step === 'ds') {
    return (
      <PageWizardChooseDatasource
        key="ds"
        onSelect={(ds) => {
          setDataSource(ds);
          setTitle(`Choose a Template for ${ds}`);
          setStep('template');
          set(maximizeSidebarWidthAtom, 'Y');
        }}
        appUid={appUid}
        width={width}
      />
    );
  }
  if (step === 'template') {
    return dataSource ? (
      <PageWizardChooseTemplateWithStore
        key="ts"
        setView={setView}
        appUid={appUid}
        ds={dataSource}
      />
    ) : (
      <PageWizardChooseTemplate key="t" setView={setView} appUid={appUid} />
    );
  }
  return (
    <div key="base" className={classes.root}>
      {appUid === 'cloudio' ? (
        <Alert variant="filled" severity="warning">
          You are trying to create a Page in cloudio schema! You may want to
          create inside your own application instead.
        </Alert>
      ) : null}
      <WizardOption
        data-testid="ds"
        iconName="database"
        title="Generate for a DataSource"
        description="Auto Generate a new Page for the existing DataSource"
        onClick={() => {
          setTitle('Choose a DataSource');
          setStep('ds');
        }}
      />
      <WizardOption
        data-testid="template"
        iconName="copy"
        title="Generate from a Template"
        description="Auto Generate a new Page using any of the existing Page Templates"
        onClick={async () => {
          setDataSource('');
          setTitle('Choose a Template');
          setStep('template');
          set(maximizeSidebarWidthAtom, 'Y');
        }}
      />
      <WizardOption
        data-testid="blank"
        iconName="edit"
        title="Blank Page"
        description="Manually design a Page from scratch"
        onClick={async () => {
          set(sidebarActivePageIdAtom, '');
          await editStore.clear();
          await editStore.createNew({
            partialRecord: {
              appUid,
              md: EMPTY_PAGE,
              showInNavigationMenu: 'Y',
            },
            rs: 'I',
          });
          await listStore.createNew({
            partialRecord: {
              appUid,
              showInNavigationMenu: 'Y',
            },
            rs: 'I',
          });
          setTitle('Add New Page');
          setView('edit');
        }}
      />
    </div>
  );
};

export default memo(PageWizard);
